/**
 * Trace block dispatch — one renderer per trace ``UIComponent``.
 *
 * Mirrors the evidence-side registry in ``componentRegistry.tsx``: every
 * trace block on the wire carries a ``ui_component_type`` tag and the
 * panel resolves it through ``traceComponentRegistry`` instead of
 * switching on ``block.tool`` / ``block.trace_type`` strings.
 *
 * Renderers return a ``TraceStepData`` (icon + header + body + class)
 * so the panel keeps full control of the accordion shell. ``null``
 * means "hide this step entirely" — used for internal tools like
 * ``working_memory`` whose results are noise to the customer.
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import tickIcon from "../../assets/circular-tick-white-bg.svg";
import { UIComponent } from "../../types/uiComponent";
import { describeToolActivity } from "../EvidencePeek/traceActivity";
import { isEvidencePeekEnabled } from "../../utils/featureFlags";
import {
	formatToolResultAsMarkdown,
	formatToolResultBody,
	summarizeAnalyzeDocumentResult,
	summarizeCatalogMarkdown,
	summarizeComputeResult,
} from "../../utils/toolResultCurators";
import type { TraceBlock } from "../Chat/Chat.types";

export interface TraceStepData {
	icon: React.ReactNode;
	header: React.ReactNode;
	body: React.ReactNode;
	stepClass: string;
}

export type TraceRenderer = (block: TraceBlock) => TraceStepData | null;

// Internal identifiers that should never appear in the customer-facing
// args chip row. The agent uses these to address resources internally.
const INTERNAL_ARG_FIELDS = new Set([
	"doc_id",
	"artifact_id",
	"attachment_id",
	"id",
]);

// Tools whose results are pure planner state and would only confuse the
// customer if shown. Backend still emits them so the trace stays
// debuggable in logs; the UI just skips them.
const HIDDEN_TOOLS = new Set(["working_memory"]);

const tickStep = (
	header: React.ReactNode,
	body: React.ReactNode,
	failed = false,
): TraceStepData => ({
	icon: <img src={tickIcon} alt="" className="trace-step-tick" />,
	header,
	body,
	stepClass: failed ? "trace-step trace-step-failed" : "trace-step",
});

const phaseStep = (
	header: React.ReactNode,
	body: React.ReactNode = null,
): TraceStepData => ({
	icon: <span className="trace-step-bullet" aria-hidden="true" />,
	header,
	body,
	stepClass: "trace-step trace-step-phase",
});

const toolNameLabel = (block: TraceBlock): React.ReactNode => (
	<span className="trace-step-name">{block.tool ?? "Tool"}</span>
);

const renderToolResult = (
	block: TraceBlock,
	curator?: (summary: string) => string | null,
): TraceStepData | null => {
	if (block.tool && HIDDEN_TOOLS.has(block.tool)) return null;
	const rawSummary = block.summary ?? "";
	const curated = curator ? curator(rawSummary) : null;
	const body = curated
		? formatToolResultAsMarkdown(curated)
		: rawSummary
			? formatToolResultBody(rawSummary, block.ui_component_type)
			: null;
	return tickStep(toolNameLabel(block), body, block.success === false);
};

const renderToolCall: TraceRenderer = (block) => {
	if (block.tool && HIDDEN_TOOLS.has(block.tool)) return null;
	const rawArgs = block.arguments;
	const args = rawArgs
		? Object.fromEntries(
				Object.entries(rawArgs).filter(([k]) => !INTERNAL_ARG_FIELDS.has(k)),
			)
		: null;
	const body =
		args && Object.keys(args).length > 0 ? (
			<div className="trace-tool-args">
				{Object.entries(args).map(([k, v]) => (
					<span key={k} className="trace-arg-chip">
						<span className="trace-arg-key">{k}</span>
						<span className="trace-arg-val">
							{typeof v === "object" ? JSON.stringify(v) : String(v)}
						</span>
					</span>
				))}
			</div>
		) : null;
	// Source-typed activity line ("Querying DealCloud", "Searching email", …)
	// when the evidence UI is on; otherwise keep the bare tool name unchanged.
	const activity = isEvidencePeekEnabled()
		? describeToolActivity(block.tool, block.arguments)
		: null;
	const label = activity ? (
		<span className="trace-step-phase-label inline-flex items-center gap-1.5">
			<activity.Icon className="h-3.5 w-3.5" aria-hidden />
			{activity.phrase}
		</span>
	) : (
		<span className="trace-step-phase-label">{block.tool ?? "Tool Call"}</span>
	);
	return phaseStep(label, body);
};

const renderToolResultMarkdown: TraceRenderer = (block) => {
	// `compute` falls back to the markdown variant on the BE; surface its
	// dedicated curator here so the FE doesn't have to round-trip through
	// raw JSON when the planner attaches a description.
	const curator =
		block.tool === "compute" ? summarizeComputeResult : undefined;
	return renderToolResult(block, curator);
};

const renderToolResultDocumentQA: TraceRenderer = (block) =>
	renderToolResult(block, summarizeAnalyzeDocumentResult);

// manipulate_workbook's TOOL_CALL block already shows the task in the audit
// log; its result body is the workbook output (often a large markdown/JSON
// dump) and is noise in the trace, so hide the whole step. The answer itself
// still reaches the user through the assistant message text.
const renderToolResultManipulateWorkbook: TraceRenderer = () => null;

const renderToolResultDealRoom: TraceRenderer = (block) =>
	renderToolResult(block, summarizeCatalogMarkdown);

const renderToolResultJsonTree: TraceRenderer = (block) =>
	renderToolResult(block);

// web_search's TOOL_CALL block already shows task + target_entity; the
// result body is too noisy to surface, so hide the whole step.
const renderToolResultWebSearch: TraceRenderer = () => null;

const renderPhaseBanner: TraceRenderer = (block) => {
	if (!block.content) return null;
	const isMultiLine = block.content.includes("\n");
	if (isMultiLine) {
		// Legacy thinking steps stored as markdown — render with accordion body.
		const newlineIdx = block.content.indexOf("\n");
		const firstLine = block.content
			.slice(0, newlineIdx)
			.replace(/^#+\s*/, "")
			.trim();
		// The planner emits its steps as a task list ("- [ ] ..."). Render
		// each step as its own plain line — no bullet, no checkbox, styled
		// like every other trace-markdown body. Strip the "- [ ]" / "- "
		// markers and give each step a paragraph break so it renders as a
		// separate <p> instead of collapsing into one soft-wrapped blob.
		const bodyMarkdown = block.content
			.slice(newlineIdx + 1)
			.split("\n")
			.map((line) => line.replace(/^[ \t]*-\s*(?:\[[ xX]\]\s*)?/, "").trim())
			.filter((line) => line.length > 0)
			.join("\n\n");
		return tickStep(
			<span className="trace-step-name">{firstLine || "Trace Step"}</span>,
			<div className="trace-step-body trace-markdown trace-phase-steps">
				<ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyMarkdown}</ReactMarkdown>
			</div>,
		);
	}
	return phaseStep(
		<span className="trace-step-phase-label">{block.content}</span>,
	);
};

const renderTraceErrorBanner: TraceRenderer = (block) => {
	if (!block.content) return null;
	return {
		icon: (
			<span
				className="trace-step-bullet trace-step-bullet-error"
				aria-hidden="true"
			/>
		),
		header: <span className="trace-step-error-label">{block.content}</span>,
		body: null,
		stepClass: "trace-step trace-step-error",
	};
};

const renderMemoGeneratedBanner: TraceRenderer = () =>
	tickStep(<span className="trace-step-name">Memo Generated</span>, null);

const renderCitationAuditorMetadata = (
	ev: Record<string, unknown>,
): TraceStepData => {
	const auditor = (ev.citation_auditor ?? {}) as Record<string, unknown>;
	const findings = Array.isArray(auditor.findings)
		? (auditor.findings as Array<Record<string, unknown>>)
		: [];
	const refillAttempted = Boolean(auditor.refill_attempted);
	const refillReplaced = Number(auditor.refill_replaced ?? 0);
	const refillError = auditor.refill_error
		? String(auditor.refill_error)
		: "";
	const findingLabel = findings.length === 1 ? "finding" : "findings";
	const header = (
		<span className="trace-step-name">
			Citation auditor - {findings.length} {findingLabel}
			{refillAttempted ? `, ${refillReplaced} refilled` : ""}
		</span>
	);

	const body =
		findings.length === 0 && !refillError ? (
			<div className="trace-step-body">No auditor findings.</div>
		) : (
			<div className="trace-step-body">
				{findings.length > 0 ? (
					<ul>
						{findings.map((finding, index) => {
							const slotId = String(finding.slot_id ?? `finding_${index + 1}`);
							const reason = finding.reason
								? String(finding.reason)
								: "No reason provided.";
							const severity = finding.severity
								? String(finding.severity)
								: "warning";
							return (
								<li key={`${slotId}-${index}`}>
									<strong>{slotId}</strong> ({severity}): {reason}
								</li>
							);
						})}
					</ul>
				) : null}
				{refillError ? (
					<div className="trace-step-error-label">
						Refill failed: {refillError}
					</div>
				) : null}
			</div>
		);
	return tickStep(header, body);
};

/**
 * Citation-artifact debug step — coverage line plus per-cell verification
 * failures grouped by reason code (value_mismatch, sum_unconfirmed,
 * workbook_unavailable, ...). Internal/debug; shown only in the trace panel.
 */
const renderCitationArtifactMetadata: TraceRenderer = (block) => {
	const ev = block.evidence;
	if (!ev) return null;
	if (ev.citation_auditor) {
		return renderCitationAuditorMetadata(ev as Record<string, unknown>);
	}
	const coverage = (ev.coverage ?? {}) as Record<string, unknown>;
	const cells = Array.isArray(ev.unverified_cells)
		? (ev.unverified_cells as Array<Record<string, unknown>>)
		: [];
	const verified = Number(coverage.verified ?? 0);
	const total = Number(coverage.total_required ?? 0);
	const notApplicable = Number(coverage.not_applicable ?? 0);
	const notPresent = Number(coverage.not_present ?? 0);
	const accountable = Math.max(0, total - notApplicable - notPresent);
	const status = coverage.status ? String(coverage.status) : "";

	// Group failures by reason code, keeping one example message each.
	const groups = new Map<string, { count: number; message?: string }>();
	for (const c of cells) {
		const code = String(c.code ?? c.status ?? "unknown");
		const g = groups.get(code) ?? { count: 0 };
		g.count += 1;
		if (!g.message && c.message) g.message = String(c.message);
		groups.set(code, g);
	}

	const header = (
		<span className="trace-step-name">
			Citation verification · {verified}/{accountable} verified
			{notPresent > 0 ? `, ${notPresent} not present` : ""}
			{status ? ` (${status})` : ""}
		</span>
	);
	const body =
		groups.size === 0 ? (
			<div className="trace-step-body">All cited values verified.</div>
		) : (
			<ul className="trace-step-body">
				{[...groups.entries()].map(([code, g]) => (
					<li key={code}>
						<strong>{code}</strong> ×{g.count}
						{g.message ? ` — ${g.message}` : ""}
					</li>
				))}
			</ul>
		);
	return tickStep(header, body);
};

/**
 * Trace dispatcher. Every value in `UIComponent` that the BE may attach
 * to a `TraceBlock` has an entry. Internal sentinels (e.g.
 * INTERNAL_EVIDENCE_METADATA) are explicitly `null` — the streaming
 * worker lifts those into top-level metadata before they reach the
 * panel, so a `null` arrival is a contract violation but renders
 * harmlessly as a hidden step.
 */
export const traceComponentRegistry: Partial<
	Record<UIComponent, TraceRenderer | null>
> = {
	[UIComponent.TOOL_CALL]: renderToolCall,
	[UIComponent.TOOL_RESULT_MARKDOWN]: renderToolResultMarkdown,
	[UIComponent.TOOL_RESULT_DOCUMENT_QA]: renderToolResultDocumentQA,
	[UIComponent.TOOL_RESULT_MANIPULATE_WORKBOOK]: renderToolResultManipulateWorkbook,
	[UIComponent.TOOL_RESULT_DEAL_ROOM]: renderToolResultDealRoom,
	[UIComponent.TOOL_RESULT_JSON_TREE]: renderToolResultJsonTree,
	[UIComponent.TOOL_RESULT_WEB_SEARCH]: renderToolResultWebSearch,
	[UIComponent.PHASE_BANNER]: renderPhaseBanner,
	[UIComponent.TRACE_ERROR_BANNER]: renderTraceErrorBanner,
	[UIComponent.MEMO_GENERATED_BANNER]: renderMemoGeneratedBanner,
	[UIComponent.INTERNAL_EVIDENCE_METADATA]: null,
	[UIComponent.CITATION_ARTIFACT_METADATA]: renderCitationArtifactMetadata,
};

/**
 * Resolve a TraceBlock to its rendered `TraceStepData`, or `null` to
 * hide. The dispatch is a single map lookup on `block.ui_component_type`
 * — there is no string-switching on `block.tool` or `block.trace_type`
 * outside the per-renderer body (where tool name is a presentational
 * detail, not a dispatch key).
 */
export const renderTraceBlock = (block: TraceBlock): TraceStepData | null => {
	const ui = block.ui_component_type as UIComponent | undefined;
	if (!ui) return null;
	const entry = traceComponentRegistry[ui];
	if (!entry) return null;
	return entry(block);
};

/** Test hook — exposes the registry shape without leaking it. */
export const __traceRegistryForTests = traceComponentRegistry;
